import { toast } from 'sonner';
import { ApiError } from '@/shared/api/api-client';

/**
 * Generic catch-all message — used whenever we can't extract a more
 * specific server message. Mirrors Flutter's fallback toast text.
 */
const GENERIC_MESSAGE = 'Something went wrong, please contact Scrips support.';

/**
 * ASP.NET Core ValidationProblemDetails shape returned for 400s.
 * Mirrors Flutter `ApiExceptionModel`:
 *   { title?, status?, errors?: Record<string, unknown[]> }
 *
 * Scrips services also return a custom `{ messages: string[] }` shape (e.g. the Inventory
 * exception middleware: `{"messages":["Move or zero this store's stock before deleting it"]}`),
 * so we read that too — otherwise those actionable 400s degrade to the generic fallback.
 */
interface ValidationProblemDetails {
  title?: unknown;
  status?: unknown;
  errors?: Record<string, unknown>;
  messages?: unknown;
}

/**
 * Surface a server / network failure as a top-right toast.
 *
 * Mirrors Flutter `handleFailure()` (scrips_core/utils/utils.dart:400):
 *   - 400  → flatten the server's field errors and show them, so the
 *            user sees e.g. "Headers are missing: Service Name, …".
 *   - 404  → body text, or "Server not reachable".
 *   - else → generic "Something went wrong" toast.
 *
 * The raw error always goes to `console.error` for triage regardless.
 *
 * @param context — short label that goes to console.error for triage
 * @param err     — the actual error / exception to log
 */
export function showServerError(context: string, err: unknown): void {
  // eslint-disable-next-line no-console
  console.error(`[${context}]`, err);
  toast.error(messageFor(err));
}

/** Surface an action-success toast. Message can be specific. */
export function showSuccess(message: string): void {
  toast.success(message);
}

/**
 * Surface a client-side validation warning (e.g. a required field left
 * blank before submitting a filter). Mirrors Flutter's
 * `ToastUtils.showErrorToast` for in-form guards — distinct from
 * `showServerError`, which is for API/network failures.
 */
export function showWarning(message: string): void {
  toast.warning(message);
}

/**
 * The user-facing text for a failure, without showing a toast. Use it to
 * render a server reason inline (for example inside a dialog).
 */
export function serverErrorMessage(err: unknown): string {
  return messageFor(err);
}

function messageFor(err: unknown): string {
  if (!(err instanceof ApiError)) return GENERIC_MESSAGE;

  if (err.status === 400) {
    const fromBody = parseValidationProblem(err.body);
    if (fromBody) return fromBody;
    return 'Request fields are missing.';
  }

  if (err.status === 404) {
    const trimmed = err.body?.trim();
    return trimmed && trimmed.length > 0 && trimmed.length < 200
      ? trimmed
      : 'Server not reachable';
  }

  // 401 is supposed to be handled by the api-client's refresh-on-401
  // retry; if it bubbles up here the refresh failed and we just want
  // the generic message. 402-499 (other), 5xx, and network failures
  // also fall through to generic.
  return GENERIC_MESSAGE;
}

/**
 * Parse a 400 response body and return the flattened error string in
 * Flutter's exact format:
 *
 *   errors.values.map(e => e.join(", ")).join("\n")
 *
 * Returns `null` if the body isn't JSON or doesn't have an `errors`
 * object with at least one non-empty array.
 *
 * Exported so tests can exercise sad paths (malformed JSON, missing
 * errors, nested arrays, non-string entries) directly. The global
 * QueryCache / MutationCache `onError` handler depends on this never
 * throwing — anything it can't parse should round-trip to `null` so
 * `messageFor` falls back to a safe default.
 */
export function parseValidationProblem(rawBody: string): string | null {
  if (!rawBody) return null;
  let parsed: ValidationProblemDetails | null = null;
  try {
    parsed = JSON.parse(rawBody) as ValidationProblemDetails;
  } catch {
    return null;
  }
  const lines: string[] = [];
  const errors = parsed?.errors;
  if (errors && typeof errors === 'object') {
    for (const value of Object.values(errors)) {
      if (Array.isArray(value)) {
        const joined = value.map((v) => String(v).trim()).filter(Boolean).join(', ');
        if (joined) lines.push(joined);
      } else if (typeof value === 'string' && value.trim().length > 0) {
        lines.push(value.trim());
      }
    }
  }

  // Scrips custom `{ messages: string[] }` shape (Inventory et al.). Read it when the ASP.NET
  // `errors` map yielded nothing, so actionable messages (dup-name, delete-guard) surface verbatim.
  if (lines.length === 0 && Array.isArray(parsed.messages)) {
    for (const m of parsed.messages) {
      if (typeof m === 'string' && m.trim().length > 0) lines.push(m.trim());
    }
  }

  if (lines.length === 0) {
    // No field-level details — fall back to the top-level title.
    const title = typeof parsed.title === 'string' ? parsed.title.trim() : '';
    return title || null;
  }
  return lines.join('\n');
}
